import { ChangeEvent, useRef, useState, useEffect } from "react";
import { useStoreDispatch } from "../../../../store/hooks";
import { setSelectedBulkFileUpload } from "../../../../store/api/admin/reducers";
import { ISiteOfflineDetails } from "../../../../store/api/devices/types";
import { useSelector } from "react-redux";
import { siteOfflineSelector } from "../../../../store/api/devices/selectors";
import { setSiteOfflineDevice } from "../../../../store/api/devices/reducers";
import { errorMsgSelector } from "../../../../store/helpers/helperSelectors";
import { setErrorMsg } from "../../../../store/helpers/helperReducers";
import { currentSiteGroupsSelector, selectedFileBulkSelector, selectedGeoRegionIdsSelector } from "../../../../store/api/admin/selectors";
import { getGroupList, getProtocolList } from "../../helpers";
import { SiteOptions, SnmpVersions, AuthenticationOptions, PrivacyOptions } from "../../../../globals/constants";
import { useGetSupportedDasModelsQuery } from "../../../../store/api/devices/deviceApiSlice";
import StatusIcon from "../../../../components/Shared/Status/StatusIcon";
import Password from "../../../../components/Shared/Inputs/Password";
import { isIPv4 } from "../../../../utils/validations";
import { useRegionDisplay } from "../../AccessControls/Regions/hooks";
import { InputField } from "../../../../components/Shared/Radix/Inputs/InputField";
import { Label } from "../../../../components/ui/label";
import { Select } from "../../../../components/ui/select";
import { RadixSelectControl } from "../../../../components/ui/radix-select";
import { Badge } from "../../../../components/ui/badge";
import { CloudUpload, X } from "lucide-react";
import FileUpload from "../../../../components/ui/file-upload";




export const AddSiteFirstStep = () => {
  const dispatch = useStoreDispatch();
  const errorMsg = useSelector(errorMsgSelector);
  const siteOfflineDetails: ISiteOfflineDetails = useSelector(siteOfflineSelector);
  const currentSiteGroups = useSelector(currentSiteGroupsSelector);
  const selectedGeoRegionIds = useSelector(selectedGeoRegionIdsSelector) || [];
  const { geoRegions } = useRegionDisplay();
  const [selectedRegionKey, setSelectedRegionKey] = useState<string>("");
  const ALLOWED_PATTERN = /^[a-zA-Z0-9-_]*$/;

  useEffect(() => {
    if (!selectedRegionKey) {
      const availableRegions = (geoRegions || []).filter((region: any) =>
        !selectedGeoRegionIds?.length || selectedGeoRegionIds.includes(region.objectKey)
      );

      const defaultRegion =
        availableRegions.find((region: any) =>
          currentSiteGroups?.some((group: any) => group.parentKey === region.objectKey)
        ) || availableRegions[0];

      if (defaultRegion && defaultRegion.objectKey) {
        setSelectedRegionKey(defaultRegion.objectKey);
      }
    }
  }, [selectedRegionKey, geoRegions, selectedGeoRegionIds, currentSiteGroups]);

  const handleRegionChange = (value: string) => {
    const val = value === "placeholder" ? "" : value;
    setSelectedRegionKey(val);
    // Reset selected group when region changes
    dispatch(setSiteOfflineDevice({ ...siteOfflineDetails, group: "" }));
    dispatch(setErrorMsg({ ...errorMsg, group: "Group is required!" }));
  };
  const isDas = (device: ISiteOfflineDetails) : boolean => {
      return device.siteType === "DAD" ;
    };

  const handleOnChange = (name: string, value: string) => {
    if ((name === "siteName" || name === "serialNumber") && value && !ALLOWED_PATTERN.test(value)) {
      dispatch(setErrorMsg({ ...errorMsg, [name]: "Only alphanumeric characters, - and _ are allowed." }));
      dispatch(setSiteOfflineDevice({ ...siteOfflineDetails, [name]: value }));
      return;
    }   
    dispatch(setSiteOfflineDevice({ ...siteOfflineDetails, [name]: value }));
    if ((name === "group" || name === "siteType") && !value) {
      dispatch(setErrorMsg({ ...errorMsg, [name]: `${name === "group" ? "Group" : "Site type"} is required!` }));
    } else {
      dispatch(setErrorMsg({ ...errorMsg, [name]: "" }));
    }
  };

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = event.target;
    handleOnChange(name, value);
  };

  const onBlurHandler = (event: any) => {
    const { name, value } = event.target;
    // Validation for Hostname and Serial Number
    if ((name === "siteName" || name === "serialNumber") && value && !ALLOWED_PATTERN.test(value)) {
      dispatch(setErrorMsg({ ...errorMsg, [name]: "Only alphanumeric characters, - and _ are allowed." }));
      return;
    }
    if (name === "siteName") {
      if (!value) dispatch(setErrorMsg({ ...errorMsg, [name]: "Hostname is required!" }));
      else dispatch(setErrorMsg({ ...errorMsg, [name]: "" }));
    }
    if (name === "serialNumber") {
      if (!value) dispatch(setErrorMsg({ ...errorMsg, [name]: "serialNumber is required!" }));
      else dispatch(setErrorMsg({ ...errorMsg, [name]: "" }));
    }
    if (name === "group") {
      if (!value) dispatch(setErrorMsg({ ...errorMsg, [name]: "Group is required!" }));
      else dispatch(setErrorMsg({ ...errorMsg, [name]: "" }));
    }
    if (name === "siteType") {
      if (!value) dispatch(setErrorMsg({ ...errorMsg, [name]: "Site type is required!" }));
      else dispatch(setErrorMsg({ ...errorMsg, [name]: "" }));
    }
  };

  const filteredSiteGroups = (currentSiteGroups || []).filter((group: any) =>
    !selectedRegionKey || group.parentKey === selectedRegionKey
  );
  
  const regionOptions = (geoRegions || [])
    .filter((region: any) =>
      (!selectedGeoRegionIds?.length || selectedGeoRegionIds.includes(region.objectKey)) &&
      (currentSiteGroups || []).some((group: any) => group.parentKey === region.objectKey)
    )
    .map((region: any) => ({ label: region.name, value: region.objectKey }));

  const groupOptions = (getGroupList(filteredSiteGroups) || []).map((o: any) => ({
    value: o.value,
    label: o.text,
  }));

  const siteTypeOptions = (getProtocolList(SiteOptions) || []).map((o: any) => ({
    value: o.value,
    label: o.text,
  }));

  return (
    <div className="flex flex-col space-y-5">
      <div className="space-y-2">
        <Label htmlFor="region">Region</Label>
        <RadixSelectControl
          value={selectedRegionKey || "placeholder"}
          options={[{ value: 'placeholder', label: 'Select Region' }, ...regionOptions]}
          onValueChange={handleRegionChange}
          autoFocus={true}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="group" className="mb-3">Group</Label>
        <RadixSelectControl
          value={siteOfflineDetails?.group || "placeholder"}
          options={[{ value: 'placeholder', label: 'Select Group' }, ...groupOptions]}
          onValueChange={(val) => handleOnChange("group", val === "placeholder" ? "" : val)}
        />
        {errorMsg?.group && <p className="text-sm text-destructive">{errorMsg.group}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="siteType" className="mb-3">Site type</Label>
        <RadixSelectControl
          value={siteOfflineDetails?.siteType || "placeholder"}
          options={[{ value: 'placeholder', label: 'Select Site type' }, ...siteTypeOptions]}
          onValueChange={(val) => handleOnChange("siteType", val === "placeholder" ? "" : val)}
        />
        {errorMsg?.siteType && <p className="text-sm text-destructive">{errorMsg.siteType}</p>}
      </div>

      <InputField
        label="Host Name"
        name="siteName"
        required
        value={siteOfflineDetails?.siteName}
        onChange={handleOnChange}
        onBlur={onBlurHandler}
        pattern={ALLOWED_PATTERN}
        patternMsg="Only alphanumeric characters, - and _ are allowed."
        placeholder="Enter host name"
      />

      <InputField
        label="Serial Number"
        name="serialNumber"
        required
        value={siteOfflineDetails?.serialNumber}
        onChange={handleOnChange}
        onBlur={onBlurHandler}
        pattern={ALLOWED_PATTERN}
        placeholder="Enter serial number"
        patternMsg="Only alphanumeric characters, - and _ are allowed."
      />
    </div>
  );
};

export const AddSiteSecondStep = () => {
  const dispatch = useStoreDispatch();
  const siteOfflineDetails: ISiteOfflineDetails = useSelector(siteOfflineSelector);
  const errorMsg = useSelector(errorMsgSelector);

  const { data: supportedDasModels = [], isLoading: isModelsLoading, isError: isModelsError } = useGetSupportedDasModelsQuery();

  const SCOS_VERSION_PATTERN = /^(\d+\.){2}\d+$/;

  const isDas = (device: ISiteOfflineDetails) : boolean => {
      return device.siteType === "DAS";
    };

  const isSnmpVersionV2c = (device: ISiteOfflineDetails) : boolean => {
      return device.snmpVersion === "1";
    };

  const handleOnChange = (name: string, value: string) => {
    if (name === "version") {
      if (value && !SCOS_VERSION_PATTERN.test(value)) {
        dispatch(setErrorMsg({ ...errorMsg, [name]: "SCOS Version must be in the format N.N.N (e.g., 10.12.321) and only numbers and dots are allowed." }));
        dispatch(setSiteOfflineDevice({ ...siteOfflineDetails, [name]: value }));
        return;
      }
    }
    if (name === "latitude") {
      const num = Number(value);
      if (
        value &&
        !(
          (!isNaN(num) && ((num >= -90000000 && num <= 90000000) || num === 2147483647))
        )
      ) {
        dispatch(setErrorMsg({ ...errorMsg, [name]: "Latitude must be between -90000000 and 90000000, or exactly 2147483647." }));
        dispatch(setSiteOfflineDevice({ ...siteOfflineDetails, [name]: value }));
        return;
      }
    }
    // Longitude validation
    if (name === "longitude") {
      const num = Number(value);
      if (
        value &&
        !(
          (!isNaN(num) && ((num >= -180000000 && num <= 180000000) || num === 2147483647))
        )
      ) {
        dispatch(setErrorMsg({ ...errorMsg, [name]: "Longitude must be between -180000000 and 180000000, or exactly 2147483647." }));
        dispatch(setSiteOfflineDevice({ ...siteOfflineDetails, [name]: value }));
        return;
      }
    }
    //Model Number validation 
    if (name === "modelNumber") {
      const siteType = siteOfflineDetails?.siteType;
      if (
        (["UMTSONLY", "LTEONLY", "UMTSAndLTE"].includes(siteType) && !value.startsWith("SCSN-")) ||
        (siteType === "NR" && !(value.startsWith("SCSN-1000") || value.startsWith("CU-") || value.startsWith("ME1200")))
      ) {
        let errorMsgText = "";
        if (["UMTSONLY", "LTEONLY", "UMTSAndLTE"].includes(siteType)) {
          errorMsgText = `Model Number must start with "SCSN-" for ${siteType} Site Type.`;
        } else if (siteType === "NR") {
          errorMsgText = 'Model Number must start with "SCSN-1000", "CU-", or "ME1200" for NR Site Type.';
        }
        dispatch(setErrorMsg({ ...errorMsg, [name]: errorMsgText }));
        dispatch(setSiteOfflineDevice({ ...siteOfflineDetails, [name]: value }));
        return;
      }
    }
  
    dispatch(setSiteOfflineDevice({ ...siteOfflineDetails, [name]: value }));
    dispatch(setErrorMsg({ ...errorMsg, [name]: "" }));
  };

  const onBlurHandler = (event: any) => {
    const { name, value } = event.target;
    // SCOS Version validation
    if (name === "version") {
      if (!value) {
        dispatch(setErrorMsg({ ...errorMsg, [name]: "SCOS Version is required!" }));
        return;
      }
      if (!SCOS_VERSION_PATTERN.test(value)) {
        dispatch(setErrorMsg({ ...errorMsg, [name]: "SCOS Version must be in the format N.N.N (e.g., 10.12.321) and only numbers and dots are allowed." }));
        return;
      }
      dispatch(setErrorMsg({ ...errorMsg, [name]: "" }));
      return;
    }
    // Latitude validation on blur
    if (name === "latitude") {
      const num = Number(value);
      if (
        isNaN(num) ||
        !((num >= -90000000 && num <= 90000000) || num === 2147483647)
      ) {
        dispatch(setErrorMsg({ ...errorMsg, [name]: "Latitude must be between -90000000 and 90000000, or exactly 2147483647." }));
        return;
      }
      dispatch(setErrorMsg({ ...errorMsg, [name]: "" }));
      return;
    }
    // Longitude validation on blur
    if (name === "longitude") {
      const num = Number(value);
      if (
        isNaN(num) ||
        !((num >= -180000000 && num <= 180000000) || num === 2147483647)
      ) {
        dispatch(setErrorMsg({ ...errorMsg, [name]: "Longitude must be between -180000000 and 180000000, or exactly 2147483647." }));
        return;
      }
      dispatch(setErrorMsg({ ...errorMsg, [name]: "" }));
      return;
    }
    // Model Number mandatory
    if (name === "modelNumber") {
      const siteType = siteOfflineDetails?.siteType;
      if (!value) {
        dispatch(setErrorMsg({ ...errorMsg, [name]: "Model Number is required!" }));
        return;
      }
      if (
        (["UMTSONLY", "LTEONLY", "UMTSAndLTE"].includes(siteType) && !value.startsWith("SCSN-")) ||
        (siteType === "NR" && !(value.startsWith("SCSN-1000") || value.startsWith("CU-") || value.startsWith("ME1200")))
      ) {
        let errorMsgText = "";
        if (["UMTSONLY", "LTEONLY", "UMTSAndLTE"].includes(siteType)) {
          errorMsgText = `Model Number must start with "SCSN-" for ${siteType} Site Type.`;
        } else if (siteType === "NR") {
          errorMsgText = 'Model Number must start with "SCSN-1000", "CU-", or "ME1200" for NR Site Type.';
        }
        dispatch(setErrorMsg({ ...errorMsg, [name]: errorMsgText }));
        return;
      }    
      dispatch(setErrorMsg({ ...errorMsg, [name]: "" }));
    }
    if(name === "ipAddress" && value){
      if(!isIPv4(value)){
        dispatch(setErrorMsg({ ...errorMsg, [name]: "Invalid IPv4 Address." }));
        return;
      }
      dispatch(setErrorMsg({ ...errorMsg, [name]: "" }));
      return;
    }

    if(name === "snmpPort" && value){
      const num = Number(value);
      if(isNaN(num) || num < 1 || num > 65535){
        dispatch(setErrorMsg({ ...errorMsg, [name]: "SNMP Port must be between 1 and 65535." }));
        return;
      }
      dispatch(setErrorMsg({ ...errorMsg, [name]: "" }));
      return;
    }

    if(name === "urlAddress" && value){
      const num = Number(value);
      if(isNaN(num) || !Number.isInteger(num) || num < 1 || num > 65535){
        dispatch(setErrorMsg({ ...errorMsg, [name]: "Device UI Port must be between 1 and 65535." }));
        return;
      }
      dispatch(setErrorMsg({ ...errorMsg, [name]: "" }));
      return;
    }

    if(name === "timeout" && value){
      const num = Number(value);
      if(isNaN(num) || num < 1 || num > 300){
        dispatch(setErrorMsg({ ...errorMsg, [name]: "Timeout must be between 1 and 300." }));
        return;
      }
      dispatch(setErrorMsg({ ...errorMsg, [name]: "" }));
      return;
    }
  };

  return (
    <div className="flex flex-col space-y-5">
      {!isDas(siteOfflineDetails) && (
        <>
      <InputField
        label="Description"
        name="description"
        value={siteOfflineDetails?.description}
        onChange={handleOnChange}
        onBlur={onBlurHandler}
        placeholder="Enter description"
      />

      <InputField
        label="SCOS Version"
        name="version"
        required
        value={siteOfflineDetails?.version}
        onChange={handleOnChange}
        onBlur={onBlurHandler}
        pattern={SCOS_VERSION_PATTERN}
         placeholder="Enter COS version"
        patternMsg="SCOS Version must be in the format N.N.N (e.g., 10.12.321) and only numbers and dots are allowed."
      />

      <InputField
        label="Model Number"
        name="modelNumber"
        required
        value={siteOfflineDetails?.modelNumber}
        onChange={handleOnChange}
        onBlur={onBlurHandler}
        placeholder="Enter model number"
      />
     </>
       )}
       {isDas(siteOfflineDetails) && (
        <div className="space-y-2">
          <Label htmlFor="modelNumber">Model Number <span className="text-destructive ml-0.5">*</span></Label>
          <Select
            id="modelNumber"
            name="modelNumber"
            value={siteOfflineDetails?.modelNumber || ''}
            options={[
              { value: '', label: isModelsLoading ? 'Loading...' : isModelsError ? 'Error loading models' : 'Select Model Number' },
              ...supportedDasModels.map(m => ({ value: m, label: m }))
            ]}
            onChange={(e) => handleOnChange("modelNumber", e.target.value)}
          />
          {errorMsg?.modelNumber && <p className="text-sm text-destructive">{errorMsg.modelNumber}</p>}
        </div>
       )}

      <InputField
        label="Latitude"
        name="latitude"
        value={siteOfflineDetails?.latitude}
        onChange={handleOnChange}
        onBlur={onBlurHandler}
        placeholder="Enter latitude"
      />

      <InputField
        label="Longitude"
        name="longitude"
        value={siteOfflineDetails?.longitude}
        onChange={handleOnChange}
        onBlur={onBlurHandler}
        placeholder="Enter longitude"
      />
       {isDas(siteOfflineDetails) && (
        <>
          <InputField
            label="IP Address"
            name="ipAddress"
            required={true}
            value={siteOfflineDetails?.ipAddress}
            onBlur={onBlurHandler}
            onChange={handleOnChange}
          />

          <InputField
            label="SNMP Port"
            name="snmpPort"
            type="number"
            required={true}
            value={siteOfflineDetails?.snmpPort}
            onBlur={onBlurHandler}
            onChange={handleOnChange}
          />

          <InputField
            label="Device UI Port"
            name="urlAddress"
            type="number"
            placeholder="Default 443"
            value={siteOfflineDetails?.urlAddress || ""}
            onBlur={onBlurHandler}
            onChange={handleOnChange}
          />

          <InputField
            label="Timeout"
            name="timeout"
            type="number"
            required={true}
            value={siteOfflineDetails?.timeout}
            onBlur={onBlurHandler}
            onChange={handleOnChange}
          />

          <div className="space-y-2">
            <Label htmlFor="snmpVersion">SNMP Version <span className="text-destructive ml-0.5">*</span></Label>
            <Select
              id="snmpVersion"
              name="snmpVersion"
              value={siteOfflineDetails?.snmpVersion || ''}
              options={[
                { value: '', label: 'Select SNMP Version' },
                ...(getProtocolList(SnmpVersions) || []).map(o => ({ value: o.value, label: o.text }))
              ]}
              onChange={(e) => handleOnChange("snmpVersion", e.target.value)}
            />
            {errorMsg?.snmpVersion && <p className="text-sm text-destructive">{errorMsg.snmpVersion}</p>}
          </div>

        {isSnmpVersionV2c(siteOfflineDetails) ? (
          <>
            <InputField
              label="Read Community"
              name="readCommunity"
              value={siteOfflineDetails?.readCommunity}
              onBlur={onBlurHandler}
              required={true}
              onChange={handleOnChange}
            />
            <InputField
              label="Write Community"
              name="writeCommunity"
              value={siteOfflineDetails?.writeCommunity}
              onBlur={onBlurHandler}
              required={true}
              onChange={handleOnChange}
            />
          </>
        ) : (
          <>
            <InputField
              label="User Name"
              name="username"
              value={siteOfflineDetails?.username}
              onBlur={onBlurHandler}
              onChange={handleOnChange}
              required={true}
            />
            <InputField
              label="Context Name"
              name="contextName"
              value={siteOfflineDetails?.contextName}
              onBlur={onBlurHandler}
              onChange={handleOnChange}
            />

            <div className="space-y-2">
              <Label htmlFor="authProtocol">Authentication Protocol <span className="text-destructive ml-0.5">*</span></Label>
              <Select
                id="authProtocol"
                name="authProtocol"
                value={siteOfflineDetails?.authProtocol || ''}
                options={[
                  { value: '', label: 'Select Authentication Protocol' },
                  ...(getProtocolList(AuthenticationOptions) || []).map(o => ({ value: o.value, label: o.text }))
                ]}
              onChange={(e) => {
                const newProtocol = e.target.value;
                if (newProtocol === 'NO_AUTH') {
                  dispatch(setSiteOfflineDevice({ ...siteOfflineDetails, authProtocol: newProtocol, authPassword: '' }));
                  dispatch(setErrorMsg({ ...errorMsg, authProtocol: "", authPassword: "" }));
                } else {
                  handleOnChange("authProtocol", newProtocol);
                }
              }}
              />
              {errorMsg?.authProtocol && <p className="text-sm text-destructive">{errorMsg.authProtocol}</p>}
            </div>

            {siteOfflineDetails?.authProtocol !== 'NO_AUTH' && (
            <Password
              label="Authentication Password"
              name="authPassword"
              value={siteOfflineDetails?.authPassword}
              onBlur={onBlurHandler}
              onChange={(event) => handleOnChange("authPassword", event.target.value)}
              required={siteOfflineDetails?.authProtocol !== 'NO_AUTH'}
            />
            )}

            <div className="space-y-2">
              <Label htmlFor="privacyProtocol">Privacy Protocol <span className="text-destructive ml-0.5">*</span></Label>
              <Select
                id="privacyProtocol"
                name="privacyProtocol"
                value={siteOfflineDetails?.privacyProtocol || ''}
                options={[
                  { value: '', label: 'Select Privacy Protocol' },
                  ...(getProtocolList(PrivacyOptions) || []).map(o => ({ value: o.value, label: o.text }))
                ]}
              onChange={(e) => {
                const newProtocol = e.target.value;
                if (newProtocol === 'NO_PRIV') {
                  dispatch(setSiteOfflineDevice({ ...siteOfflineDetails, privacyProtocol: newProtocol, privacyPassword: '' }));
                  dispatch(setErrorMsg({ ...errorMsg, privacyProtocol: "", privacyPassword: "" }));
                } else {
                  handleOnChange("privacyProtocol", newProtocol);
                }
              }}
              />
              {errorMsg?.privacyProtocol && <p className="text-sm text-destructive">{errorMsg.privacyProtocol}</p>}
            </div>

            {siteOfflineDetails?.privacyProtocol !== 'NO_PRIV' && (
            <Password
              label="Privacy Password"
              name="privacyPassword"
              value={siteOfflineDetails?.privacyPassword}
              onBlur={onBlurHandler}
              onChange={(event) => handleOnChange("privacyPassword", event.target.value)}
              required={siteOfflineDetails?.privacyProtocol !== 'NO_PRIV'}
            />
            )}
          </>
        )}        
        </>
      )}
    </div>
  );
};

export const BulkSiteUpload = () => {
  const dispatch = useStoreDispatch();
  const selectedFile = useSelector(selectedFileBulkSelector);
  const errorMsg = useSelector(errorMsgSelector);

  const handleSetFile = (fileRes: File | null) => {
    dispatch(setSelectedBulkFileUpload(fileRes));
    if (errorMsg?.fileSupport) {
      dispatch(setErrorMsg({ ...errorMsg, fileSupport: "" }));
    }
  };

  return (
    <div className="flex flex-col items-start justify-between py-15 text-foreground w-full">
      <p className="mb-0.5 text-sm font-medium">Select the site list file you want to add (CSV)</p>
      <div className="mt-2 w-full">
        <FileUpload
          file={selectedFile}
          setFile={handleSetFile}
          accept=".csv"
          allowedTypes={["text/csv", "application/csv"]}
          maxSizeMB={10}
        />
      </div>
      {errorMsg?.fileSupport && (
        <div className="w-full flex items-center justify-center text-destructive text-sm mt-2">{errorMsg.fileSupport}</div>
      )}
    </div>
  );
};

export const SuccessPopUp = () => {
  return (
    <div className="flex items-center justify-center gap-4 flex-col text-foreground">
      <h3 className="text-lg font-semibold">Site successfully added</h3>
      <p>Site are avilable in the devices page</p>
      <StatusIcon status={"Done"} isLabel={false} />
    </div>
  );
}

export const step1st2nd = [
  //this is comman steps for all task
  {
    title: "Choose the preferred action to be attached to this task:",
    component: <AddSiteFirstStep />,
  },
  {
    title: "Select the devices that will execute this task",
    component: <AddSiteSecondStep />,
  },
  // {
  //   title: "SuccessfullPopup",
  //   component: <SuccessPopUp />
  // },
];

export const stepsBulkUpload = [
  //this is comman steps for all task
  {
    title: "Bulk upload",
    component: <BulkSiteUpload />
  },
  // {
  //   title: "SuccessfullPopup",
  //   component: <SuccessPopUp />
  // },
];